import { useEffect, useMemo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import { Button, Spinner } from "reactstrap";
import { ControlsFrameworks } from "src/modules/data-protection/constants";
import {
    buildReplaceControlsPayload,
    buildSelectedControls,
    groupControlIdsByFramework,
} from "../helpers/controls";
import { formatControlChip } from "../helpers/measureList";
import useIsmsControlOptions from "../hooks/useIsmsControlOptions";
import useReplacePositiveListControls from "../hooks/useReplacePositiveListControls";

const selectStyles = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

const groupControls = (controls = []) => {
    const groups = new Map();

    controls.forEach((control) => {
        const framework = control?.type || control?.framework || "other";

        if (!groups.has(framework)) {
            groups.set(framework, []);
        }

        groups.get(framework).push(control);
    });

    const order = ControlsFrameworks.map((item) => item.key);

    return [...groups.entries()]
        .sort((left, right) => {
            const leftIndex = order.indexOf(left[0]);
            const rightIndex = order.indexOf(right[0]);

            return (leftIndex === -1 ? order.length : leftIndex)
                - (rightIndex === -1 ? order.length : rightIndex);
        })
        .map(([key, items]) => ({
            key,
            title: ControlsFrameworks.find((item) => item.key === key)?.title || key,
            controls: items,
        }));
};

const RelatedControlsSection = ({
    softwareId,
    measureId,
    controls = [],
    t,
    i18n,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    const [selectedByFramework, setSelectedByFramework] = useState({});
    const groups = useMemo(() => groupControls(controls), [controls]);
    const groupKey = groups.map((group) => group.key).join("|");
    const replaceMutation = useReplacePositiveListControls({
        softwareId,
        t,
        onSuccess: () => setIsEditing(false),
    });
    useEffect(() => {
        setIsEditing(false);
        setSelectedByFramework({});
        setOpenKeys(groupKey ? groupKey.split("|") : []);
    }, [measureId, groupKey]);

    const controlOptions = useIsmsControlOptions({
        softwareId,
        language: i18n?.language,
        t,
        enabled: isEditing,
    });

    const startEditing = () => {
        setSelectedByFramework(groupControlIdsByFramework(controls));
        setIsEditing(true);
    };

    const saveControls = () => {
        const payload = buildReplaceControlsPayload({
            selectedByFramework,
            linkedControls: controls,
            loadedFrameworks: controlOptions.loadedFrameworks,
        });

        replaceMutation.mutate({
            measureId,
            payload,
            selectedControls: buildSelectedControls({
                selectedByFramework,
                optionsByFramework: controlOptions.optionsByFramework,
                linkedControls: controls,
                loadedFrameworks: controlOptions.loadedFrameworks,
            }),
        });
    };

    const selectValue = (framework) => {
        const options = controlOptions.optionsByFramework[framework] || [];
        const selectedIds = selectedByFramework[framework] || [];

        return selectedIds.map((id) => {
            const option = options.find((item) => String(item.value) === String(id));

            if (option) {
                return option;
            }

            const linked = controls.find((control) => String(control.id) === String(id));

            return {
                value: id,
                label: formatControlChip(linked) || String(id),
            };
        });
    };

    return (
        <section className="positive-list-detail__card">
            {!isEditing && (
                <>
                    <div className="positive-list-detail__card-header">
                        <h6>{t("Controls")}</h6>
                        <button
                            type="button"
                            className="positive-list-detail__icon-button"
                            aria-label={t("Edit controls")}
                            onClick={startEditing}
                        >
                            <i className="ri-pencil-line" />
                        </button>
                    </div>

                    {groups.length === 0 ? (
                        <p className="positive-list-detail__text mb-0">
                            {t("No related controls.")}
                        </p>
                    ) : (
                        <Accordion
                            alwaysOpen
                            activeKey={openKeys}
                            onSelect={(keys) => setOpenKeys(Array.isArray(keys) ? keys : [])}
                            className="positive-list-detail__controls"
                        >
                            {groups.map((group) => (
                                <Accordion.Item key={group.key} eventKey={group.key}>
                                    <Accordion.Header>
                                        <span className="positive-list-detail__control-heading">
                                            <span>{t(group.title)}</span>
                                            <span className="positive-list-detail__count">
                                                {group.controls.length}
                                            </span>
                                        </span>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {group.controls.map((control) => (
                                            <div
                                                key={control.id}
                                                className="positive-list-detail__control"
                                            >
                                                {formatControlChip(control)}
                                            </div>
                                        ))}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}
                </>
            )}

            {isEditing && (
                <>
                    <h6 className="mb-1">{t("Edit controls")}</h6>
                    <p className="text-muted mb-3">
                        {t(
                            "Choose the ISMS controls this measure supports. Saving replaces the current set."
                        )}
                    </p>

                    {controlOptions.isLoading && (
                        <div className="text-center py-3">
                            <Spinner size="sm" />
                        </div>
                    )}

                    {!controlOptions.isLoading && controlOptions.frameworks.length === 0 && (
                        <p className="text-muted mb-3">
                            {t("No ISMS controls are available.")}
                        </p>
                    )}

                    {!controlOptions.isLoading && controlOptions.frameworks.map((framework) => {
                        const frameworkInfo = ControlsFrameworks.find(
                            (item) => item.key === framework
                        );

                        return (
                            <div key={framework} className="mb-3">
                                <label className="form-label">
                                    {t(frameworkInfo?.title || framework)}
                                </label>
                                <Select
                                    isMulti
                                    classNamePrefix="select2-selection"
                                    options={controlOptions.optionsByFramework[framework] || []}
                                    value={selectValue(framework)}
                                    placeholder={t("Select")}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    onChange={(selected) => {
                                        setSelectedByFramework((current) => ({
                                            ...current,
                                            [framework]: (selected || []).map(
                                                (option) => option.value
                                            ),
                                        }));
                                    }}
                                />
                            </div>
                        );
                    })}

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            color="secondary"
                            outline
                            disabled={replaceMutation.isLoading}
                            onClick={() => setIsEditing(false)}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            color="primary"
                            disabled={
                                replaceMutation.isLoading
                                || controlOptions.isLoading
                                || controlOptions.frameworks.length === 0
                            }
                            onClick={saveControls}
                        >
                            {replaceMutation.isLoading && (
                                <Spinner size="sm" className="me-1" />
                            )}
                            {t("Save")}
                        </Button>
                    </div>
                </>
            )}
        </section>
    );
};

export default withNamespaces()(RelatedControlsSection);
